import React, {useState, useEffect, useRef} from 'react';
import {Autocomplete, TextField, FormControl} from '@mui/material';
import {useInfiniteQuery} from '@tanstack/react-query';
import {getRetailLocations} from '../services/RetailLocationService';

// Helper function to format retail location display
const formatRetailLocationDisplay = (option) => {
    if (!option) return '';
    if (typeof option === 'string') return option;
    const shiptoId = option.shiptoId || '';
    const displayName = option.displayName || '';
    const city = option.city || '';
    const state = option.state || '';

    // Desired format: {shiptoId}: {displayName} ({city}, {state})
    const locationPart = city || state ? ` (${city}${city && state ? ', ' : ''}${state})` : '';
    const idPart = shiptoId ? `${shiptoId}: ` : '';
    return `${idPart}${displayName}${locationPart}`.trim();
};

const getPlaceholderLabel = (label) => (label ? label.replace(/ \*$/, "") : "");

const RetailLocationSearch = ({label, businessUnitCode, billtoId, editable, required, initialValue = '', onChange}) => {

    const [open, setOpen] = useState(false);
    const [value, setValue] = useState(null);
    const [inputValue, setInputValue] = useState(initialValue);
    const [options, setOptions] = useState([]);
    const [isSearchById, setIsSearchById] = useState(false);
    const [candidateId, setCandidateId] = useState('');
    const debounceTimeout = useRef(null);

    useEffect(() => {
        if (initialValue !== '') {
            setInputValue(initialValue);
            setCandidateId(initialValue); // Set initialValue as candidate ID
        }
    }, [initialValue]);

    // Only enable the query when both businessUnitCode and billtoId are provided
    const queryEnabled = !!businessUnitCode && !!billtoId && (open || inputValue !== '');

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
    } = useInfiniteQuery({
        queryKey: ['retailLocations', businessUnitCode, billtoId, inputValue, candidateId],
        queryFn: ({pageParam}) => {
            // If we have a candidateId, use it for id search
            // Otherwise, use inputValue as searchString
            const useIdSearch = candidateId !== '';
            setIsSearchById(useIdSearch);

            return getRetailLocations(
                pageParam, 
                10, 
                useIdSearch
                    ? {businessUnitCode, billtoId, id: candidateId}
                    : {businessUnitCode, billtoId, searchString: inputValue}
            );
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
            return lastPage.retailLocations.length ? allPages.length : undefined;
        },
        enabled: queryEnabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 30 * 60 * 1000, // 30 minutes
    });

    useEffect(() => {
        if (data) {
            const fetchedOptions = data.pages.flatMap(page => page.retailLocations);
            setOptions(fetchedOptions);

            // Only auto-select when search was performed using a candidate ID
            if (candidateId) {
                // search option where id matches candidateId
                const found = fetchedOptions.find(option => option.id === Number(candidateId));
                if (found) {
                    setValue(found);
                    onChange(found);
                } else {
                    // If no matching option found, reset both candidateId and selection
                    setCandidateId('');
                    setValue(null);
                    setInputValue(''); // Clear input value
                    onChange(null);
                }
            }
        }
    }, [data, isSearchById, candidateId]);

    const handleScroll = (event) => {
        const bottom = event.target.scrollHeight - event.target.scrollTop === event.target.clientHeight;
        if (bottom && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleInputChange = (event, newInputValue) => {
        // If the input is changed by user typing (event exists), clear the selection
        if (event && event.type === 'change') {
            setValue(null);
            setCandidateId(''); // Clear candidate ID when user types
            onChange(null); // Notify parent component that selection is cleared
        }

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => {
            const substringBeforeSpace = newInputValue.split(' ')[0];
            setInputValue(substringBeforeSpace);
            if (substringBeforeSpace === '') {
                setCandidateId(''); // Clear candidate ID if input is empty
                setValue(null); // Clear selection
            }
        }, 500);
    };

    return (
        <FormControl fullWidth size="small" margin="dense" required={required}>
            <Autocomplete
                open={open}
                onOpen={() => setOpen(true)}
                onClose={handleClose}
                onChange={(event, newValue) => {
                    setValue(newValue);
                    if (newValue) {
                        setCandidateId(String(newValue.id)); // Set selected option ID as candidate ID
                    } else {
                        setCandidateId('');
                    }
                    onChange(newValue);
                }}
                getOptionLabel={(option) => formatRetailLocationDisplay(option)}
                renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                        {formatRetailLocationDisplay(option)}
                    </li>
                )}
                options={options}
                loading={isFetching}
                onInputChange={handleInputChange}
                disabled={!editable || !businessUnitCode || !billtoId}
                ListboxProps={{onScroll: handleScroll, sx: {maxHeight: 200}}}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={value}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        size="small"
                        inputProps={{...params.inputProps, placeholder: `Select ${getPlaceholderLabel(label)}`}}
                        sx={{'& input::placeholder': {color: 'rgba(0, 0, 0, 0.38)', opacity: 1}}}
                        helperText={!businessUnitCode || !billtoId ? "Business Unit and Bill-to must be selected first" : ""}
                    />
                )}
            />
        </FormControl>
    );
};

export default RetailLocationSearch;
